import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Progress,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Navbar from './Navbar';
import AuthContext from '../context/AuthContext';
import { API_BASE_URL } from '../config/adminConfig';
import {
  ENGAGEMENT_QUESTIONS,
  VERDICT_PRESENTATION,
  describeSample,
  describeWindow,
  formatRate,
  formatThreshold,
} from './engagementFormatters';

/**
 * The five engagement questions, each against the threshold that turns it into
 * a decision.
 *
 * The page is built around one refusal: a measurement with too small a sample
 * is shown as absent, not as zero. Those rows render grey with no bar, because
 * a red 0% beside a threshold reads as a failing product when the honest
 * reading is that nobody has answered the question yet.
 */
function EngagementMetrics() {
  const { token, logout } = useContext(AuthContext);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const load = useCallback(
    async (signal, range = {}) => {
      try {
        setLoading(true);
        setError('');

        const query = new URLSearchParams();
        if (range.since) query.set('since', range.since);
        if (range.until) query.set('until', range.until);
        const suffix = query.toString() ? `?${query}` : '';

        const response = await fetch(
          `${API_BASE_URL}/api/admin/cohort-metrics${suffix}`,
          { headers: { Authorization: `Bearer ${token}` }, signal },
        );

        if (response.status === 401) {
          logout();
          return;
        }
        if (!response.ok) {
          throw new Error(`Engagement request failed (${response.status})`);
        }
        setReport(await response.json());
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError('Engagement metrics are temporarily unavailable. Please retry shortly.');
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [logout, token],
  );

  useEffect(() => {
    const controller = new AbortController();
    if (token) load(controller.signal);
    return () => controller.abort();
  }, [load, token]);

  const applyRange = event => {
    event.preventDefault();
    load(undefined, { since, until });
  };

  const metrics = report?.metrics || [];

  return (
    <Box minH="100vh" bg={pageBg}>
      <Navbar />
      <Box maxW="6xl" mx="auto" p={{ base: 4, md: 6 }}>
        <Heading size="xl" mb={2}>Engagement</Heading>
        <Text color="gray.600" mb={6}>
          Five questions the app has to answer before growth is worth buying. Each
          thresholds are working hypotheses and require validation.
        </Text>

        {error ? (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Card bg={cardBg} shadow="sm" mb={6}>
          <CardBody>
            <form onSubmit={applyRange}>
              <Flex gap={4} align="flex-end" wrap="wrap">
                <FormControl maxW="200px">
                  <FormLabel fontSize="sm" mb={1}>Signed up after</FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={since}
                    onChange={event => setSince(event.target.value)}
                  />
                </FormControl>
                <FormControl maxW="200px">
                  <FormLabel fontSize="sm" mb={1}>Signed up before</FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={until}
                    onChange={event => setUntil(event.target.value)}
                  />
                </FormControl>
                <Button type="submit" size="sm" colorScheme="blue" isLoading={loading}>
                  Apply
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSince('');
                    setUntil('');
                    load(undefined, {});
                  }}
                >
                  All users
                </Button>
              </Flex>
            </form>
            <Text fontSize="sm" color="gray.500" mt={3}>
              {describeWindow(report?.window)} · {report?.cohortSize ?? 0} people
            </Text>
          </CardBody>
        </Card>

        {report?.revenue && <Alert status="info" mb={4}><AlertIcon /><Box>
          <Text>Cash revenue: not measurable. {report.revenue.reason}</Text>
          <Text>Current verified entitlements: {report.currentVerifiedEntitlements?.count ?? 0}; production: {report.currentVerifiedEntitlements?.production ?? 0}; sandbox: {report.currentVerifiedEntitlements?.sandbox ?? 0}; unknown environment: {report.currentVerifiedEntitlements?.unknownEnvironment ?? 0}.</Text>
          <Text>Explicitly excluded internal accounts: {report.excludedInternalUsers ?? 0}. {report.trackingSince ? `Tracking coverage starts ${report.trackingSince}.` : 'Tracking coverage start has not been established.'}</Text>
          {(report.limitations || []).map(item => <Text key={item} fontSize="sm">{item}</Text>)}
        </Box></Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={20}>
            <Spinner size="xl" />
          </Box>
        ) : (
          <SimpleGrid columns={1} spacing={4}>
            {metrics.map(metric => (
              <QuestionCard key={metric.key} metric={metric} bg={cardBg} />
            ))}
            {!metrics.length ? (
              <Card bg={cardBg} shadow="sm">
                <CardBody>
                  <Text color="gray.500">
                    No users match this window, so there is nothing to measure.
                  </Text>
                </CardBody>
              </Card>
            ) : null}
          </SimpleGrid>
        )}

        <Text mt={6} color="gray.500" fontSize="sm">
          Updated at: {report?.updatedAt
            ? new Date(report.updatedAt).toLocaleString()
            : 'n/a'}
        </Text>
      </Box>
    </Box>
  );
}

function QuestionCard({ metric, bg }) {
  const question = ENGAGEMENT_QUESTIONS[metric.key] || {};
  const verdict = VERDICT_PRESENTATION[metric.verdict] || VERDICT_PRESENTATION.insufficient_data;
  const measured = metric.rate !== null && metric.rate !== undefined && metric.readable;

  return (
    <Card bg={bg} shadow="sm">
      <CardBody>
        <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
          <Box flex="1" minW="260px">
            <Heading size="md" mb={1}>{question.title || metric.key}</Heading>
            <Text fontSize="sm" color="gray.600">{question.metric}</Text>
          </Box>

          <Stat maxW="180px" textAlign="right">
            <StatLabel color="gray.500">Now</StatLabel>
            <StatNumber
              color={measured ? undefined : 'gray.400'}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatRate(metric.readable ? metric.rate : null)}
            </StatNumber>
            <Badge colorScheme={verdict.scheme} mt={1}>{verdict.label}</Badge>
          </Stat>
        </Flex>

        {/* No bar when the sample is too small: an empty track would still read
            as a position on the scale, and there is no position to report. */}
        {measured ? (
          <Box mt={4}>
            <Progress
              value={Math.min(100, (metric.rate / metric.threshold) * 100)}
              colorScheme={verdict.scheme}
              size="sm"
              borderRadius="full"
            />
            <Flex justify="space-between" mt={1}>
              <Text fontSize="xs" color="gray.500">
                {describeSample(metric)}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Threshold {formatThreshold(metric.threshold)}
              </Text>
            </Flex>
          </Box>
        ) : (
          <Flex justify="space-between" mt={4}>
            <Text fontSize="xs" color="gray.500">{describeSample(metric)}</Text>
            <Text fontSize="xs" color="gray.500">
              Threshold {formatThreshold(metric.threshold)}
            </Text>
          </Flex>
        )}

        {question.rationale ? (
          <Text fontSize="sm" color="gray.500" mt={3} fontStyle="italic">
            {question.rationale}
          </Text>
        ) : null}
      </CardBody>
    </Card>
  );
}

export default EngagementMetrics;
